/**
 * AdminAssistant — Asistente IA de configuración (admin/supervisor).
 *
 * Chat flotante disponible en todo el portal. Permite configurar el sistema
 * mediante lenguaje natural usando el modelo de IA ya configurado. Fase 1:
 * horarios y suspensiones de salas. El backend (portal_asistenteAdmin) detecta
 * la intención, pide los datos faltantes, propone la acción para CONFIRMAR y la
 * ejecuta tras la confirmación del admin.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  Sparkles, X, Send, Bot, Check, Ban, Loader2, CheckCircle2, AlertTriangle, Mic, MicOff,
} from 'lucide-react';
import { LiveKitRoom, RoomAudioRenderer, useRoomContext } from '@livekit/components-react';
import { RoomEvent, type TranscriptionSegment, type Participant } from 'livekit-client';
import { functions } from '../../config/firebase';
import styles from './AdminAssistant.module.css';

interface Step { tool: string; args: Record<string, unknown>; label: string }

interface Msg {
  role: 'user' | 'assistant';
  text: string;
  confirm?: { summary: string; steps: Step[]; firma?: string };
  status?: 'pending' | 'confirmed' | 'cancelled';
  results?: Array<{ ok: boolean; label: string; error?: string }>;
}

type AsistenteResponse =
  | { kind: 'message'; text: string }
  | { kind: 'confirm'; summary: string; steps: Step[]; firma?: string }
  | { kind: 'executed'; text: string; results: Array<{ ok: boolean; label: string; error?: string }> };

const callAsistente = httpsCallable(functions, 'portal_asistenteAdmin');
const callVozToken = httpsCallable(functions, 'portal_getAsistenteVozToken');

interface VoiceConn { token: string; url: string; room: string }

/**
 * Escucha las transcripciones del room (voz del admin y del agente) y las
 * reporta: finales → burbujas persistentes; interim → caption en vivo.
 * Debe renderizarse DENTRO de <LiveKitRoom> (usa su contexto).
 */
function VoiceCaptions({
  onFinal,
  onInterim,
}: {
  onFinal: (role: 'user' | 'assistant', text: string) => void;
  onInterim: (text: string) => void;
}) {
  const room = useRoomContext();
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!room) return;
    const handler = (segments: TranscriptionSegment[], participant?: Participant) => {
      const role: 'user' | 'assistant' =
        (participant?.identity || '').startsWith('admin_') ? 'user' : 'assistant';
      let interim = '';
      for (const seg of segments) {
        if (seg.final) {
          if (!seen.current.has(seg.id)) {
            seen.current.add(seg.id);
            if (seg.text.trim()) onFinal(role, seg.text.trim());
          }
        } else if (seg.text.trim()) {
          interim = seg.text;
        }
      }
      onInterim(interim);
    };
    room.on(RoomEvent.TranscriptionReceived, handler);
    const seenSet = seen.current;
    return () => {
      room.off(RoomEvent.TranscriptionReceived, handler);
      seenSet.clear();
    };
  }, [room, onFinal, onInterim]);
  return null;
}

const INTRO: Msg = {
  role: 'assistant',
  text:
    '¡Hola! Soy tu asistente de configuración. Puedo gestionar **horarios** y **suspensiones** de las salas por ti.\n\n' +
    'Por ejemplo:\n' +
    '• «Crea un horario para la sala general los lunes de 7 a 11 de la mañana»\n' +
    '• «Cancela el horario del lunes de 7 a 3 y crea uno de 11 a 4 de la tarde»\n' +
    '• «Suspende la sala el 25 de diciembre por feriado»',
};

function salaContextFromPath(pathname: string): string | undefined {
  const m = pathname.match(/^\/config\/salas\/([^/]+)\/editar/);
  if (m && m[1] && m[1] !== 'nueva') return m[1];
  return undefined;
}

/** Render de texto simple con **negritas** y saltos de línea. */
function renderText(text: string) {
  return text.split('\n').map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return (
      <span key={i} className={styles.line}>
        {parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <strong key={j}>{p.slice(2, -2)}</strong>
            : <span key={j}>{p}</span>,
        )}
      </span>
    );
  });
}

export function AdminAssistant() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([INTRO]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // ── Modo voz (LiveKit Voice AI) ──
  const [voice, setVoice] = useState<VoiceConn | null>(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [interim, setInterim] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const salaContextId = salaContextFromPath(location.pathname);

  const startVoice = useCallback(async () => {
    if (voice || voiceLoading) return;
    setVoiceLoading(true);
    try {
      const res = await callVozToken({ salaContextId });
      setVoice(res.data as VoiceConn);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error';
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ No pude iniciar el modo voz: ${msg}` }]);
    } finally {
      setVoiceLoading(false);
    }
  }, [voice, voiceLoading, salaContextId]);

  const stopVoice = useCallback(() => {
    setVoice(null);
    setInterim('');
  }, []);

  const handleVoiceFinal = useCallback((role: 'user' | 'assistant', text: string) => {
    setMessages((prev) => [...prev, { role, text }]);
  }, []);

  // Autoscroll al fondo cuando llegan mensajes
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, loading, open]);

  // Enfocar el input al abrir
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const historyFor = useCallback(
    (msgs: Msg[]) => msgs.map((m) => ({ role: m.role, text: m.text })),
    [],
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg: Msg = { role: 'user', text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await callAsistente({ messages: historyFor(next), salaContextId });
      const data = res.data as AsistenteResponse;
      if (data.kind === 'confirm') {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: data.summary, confirm: { summary: data.summary, steps: data.steps, firma: data.firma }, status: 'pending' },
        ]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.text }]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ocurrió un error';
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ ${msg}` }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, historyFor, salaContextId]);

  const confirmAction = useCallback(async (index: number) => {
    const target = messages[index];
    if (!target?.confirm || loading) return;
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, status: 'confirmed' } : m)));
    setLoading(true);
    try {
      const res = await callAsistente({
        messages: historyFor(messages),
        execute: { steps: target.confirm.steps, firma: target.confirm.firma },
        salaContextId,
      });
      const data = res.data as AsistenteResponse;
      if (data.kind === 'executed') {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.text, results: data.results }]);
      } else if (data.kind === 'message') {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.text }]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ocurrió un error';
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ ${msg}` }]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, historyFor, salaContextId]);

  const cancelAction = useCallback((index: number) => {
    setMessages((prev) => {
      const updated = prev.map((m, i) => (i === index ? { ...m, status: 'cancelled' as const } : m));
      return [...updated, { role: 'assistant', text: 'Acción cancelada. ¿Querés ajustar algo o intentar de otra forma?' }];
    });
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className={styles.fab}
          onClick={() => setOpen(true)}
          aria-label="Asistente de configuración"
          title="Asistente de configuración (IA)"
        >
          <Sparkles size={22} />
        </button>
      )}

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Asistente de configuración">
          <div className={styles.head}>
            <div className={styles.headTitle}>
              <span className={styles.headIcon}><Bot size={16} /></span>
              <div>
                <span className={styles.headName}>Asistente de configuración</span>
                <span className={styles.headSub}>IA · horarios y suspensiones</span>
              </div>
            </div>
            <button type="button" className={styles.closeBtn} onClick={() => setOpen(false)} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>

          <div className={styles.body} ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`${styles.msg} ${m.role === 'user' ? styles.msgUser : styles.msgBot}`}>
                <div className={styles.bubble}>{renderText(m.text)}</div>

                {/* Tarjeta de confirmación de acción */}
                {m.confirm && (
                  <div className={styles.confirmCard}>
                    <div className={styles.confirmSteps}>
                      {m.confirm.steps.map((s, j) => (
                        <div key={j} className={styles.confirmStep}>
                          <span className={styles.stepDot} /> {s.label}
                        </div>
                      ))}
                    </div>
                    {m.status === 'pending' ? (
                      <div className={styles.confirmActions}>
                        <button
                          type="button"
                          className={styles.btnConfirm}
                          onClick={() => confirmAction(i)}
                          disabled={loading}
                        >
                          <Check size={14} /> Confirmar
                        </button>
                        <button
                          type="button"
                          className={styles.btnCancel}
                          onClick={() => cancelAction(i)}
                          disabled={loading}
                        >
                          <Ban size={14} /> Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className={`${styles.statusTag} ${m.status === 'confirmed' ? styles.statusOk : styles.statusCancel}`}>
                        {m.status === 'confirmed' ? 'Confirmado' : 'Cancelado'}
                      </div>
                    )}
                  </div>
                )}

                {/* Resultados de ejecución por paso */}
                {m.results && m.results.length > 0 && (
                  <div className={styles.resultsCard}>
                    {m.results.map((r, j) => (
                      <div key={j} className={`${styles.resultRow} ${r.ok ? styles.resultOk : styles.resultErr}`}>
                        {r.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                        <span>{r.label}{!r.ok && r.error ? ` — ${r.error}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className={`${styles.msg} ${styles.msgBot}`}>
                <div className={`${styles.bubble} ${styles.typing}`}>
                  <Loader2 size={15} className={styles.spin} /> Pensando…
                </div>
              </div>
            )}

            {voice && (
              <div className={styles.voiceStrip}>
                <span className={styles.voiceDot} aria-hidden="true" />
                <span className={styles.voiceStripText}>
                  {interim || 'Escuchando… habla con el asistente'}
                </span>
                <button type="button" className={styles.voiceEndBtn} onClick={stopVoice}>
                  Terminar
                </button>
              </div>
            )}
          </div>

          <div className={styles.inputBar}>
            <button
              type="button"
              className={`${styles.micBtn} ${voice ? styles.micBtnActive : ''}`}
              onClick={voice ? stopVoice : startVoice}
              disabled={voiceLoading}
              aria-label={voice ? 'Terminar modo voz' : 'Hablar con el asistente (voz)'}
              title={voice ? 'Terminar modo voz' : 'Hablar con el asistente (voz)'}
            >
              {voiceLoading
                ? <Loader2 size={16} className={styles.spin} />
                : voice ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <textarea
              ref={inputRef}
              className={styles.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={voice ? 'Modo voz activo — o escribe…' : 'Escribe una instrucción…'}
              rows={1}
            />
            <button
              type="button"
              className={styles.sendBtn}
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Enviar"
            >
              <Send size={16} />
            </button>
          </div>

          {/* Modo voz: conecta al room del agente, reproduce su voz y muestra
              las transcripciones en vivo. No renderiza UI propia. */}
          {voice && (
            <LiveKitRoom
              serverUrl={voice.url}
              token={voice.token}
              connect
              connectOptions={{
                // Estaba fijo en 'relay', lo que ataba el asistente de voz al
                // TURN: si el relay caía, no conectaba aunque hubiera ruta
                // directa disponible. 'all' prueba primero la ruta directa y
                // usa el TURN como respaldo, que es para lo que existe.
                rtcConfig: { iceTransportPolicy: 'all' },
              }}
              audio
              video={false}
              onDisconnected={stopVoice}
            >
              <RoomAudioRenderer />
              <VoiceCaptions onFinal={handleVoiceFinal} onInterim={setInterim} />
            </LiveKitRoom>
          )}
        </div>
      )}
    </>
  );
}
